import { useLocation, useParams } from 'react-router-dom'
import { ActivitySquare, Gauge, Mic2, Music2 } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { Tile } from '@/components/dashboard/StatBento'
import { RankedList } from '@/components/dashboard/RankedList'
import { SoundMix } from '@/components/dashboard/SoundMix'
import { ThemeExplorer } from '@/components/instructor/ThemeExplorer'
import { RideTeaser } from '@/components/instructor/RideTeaser'
import { CountUp } from '@/components/dashboard/fx/CountUp'
import { NameMarquee } from '@/components/dashboard/fx/NameMarquee'
import { PageHero } from '@/components/dashboard/fx/PageHero'
import { Reveal } from '@/components/dashboard/fx/Reveal'
import { SectionHeading } from '@/components/dashboard/fx/SectionHeading'
import { SpotlightCard } from '@/components/dashboard/fx/SpotlightCard'
import { SECTION } from '@/components/dashboard/fx/layout'
import type { InstructorPreview } from '@/components/layout/TransitionLink'
import { useInstructorStats } from '@/hooks/useInstructorStats'
import { usePlaylistOptions } from '@/hooks/usePlaylistOptions'
import { workoutNouns } from '@/lib/discipline'
import { initials } from '@/lib/format'

// An instructor's totals can span multiple disciplines (many teach both
// cycling and running/walking classes), so "workouts" rather than "rides".
const { singular: WORKOUT, plural: WORKOUTS, pluralCapitalized: WORKOUTS_CAPITALIZED } = workoutNouns('all')

// "Hannah Frankson" -> solid "Hannah", italic-gradient "Frankson".
function splitName(name: string) {
  const parts = name.split(' ')
  return parts.length > 1
    ? { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] }
    : { first: name, last: undefined }
}

function ShareStat({ percent, label, delay }: { percent: number; label: string; delay: number }) {
  return (
    <Reveal delay={delay}>
      <SpotlightCard className="flex h-full flex-col gap-4 p-6 sm:p-8">
        <CountUp
          value={percent}
          decimals={Number.isInteger(percent) ? 0 : 1}
          suffix="%"
          className="font-display text-[clamp(3.5rem,8vw,6.5rem)] font-semibold leading-[0.9] tracking-tighter text-gradient"
        />
        <p className="text-muted-foreground">{label}</p>
      </SpotlightCard>
    </Reveal>
  )
}

export function InstructorPage() {
  const { id } = useParams<{ id: string }>()
  const { data, error } = useInstructorStats(id)
  const { data: options, error: optionsError } = usePlaylistOptions(data?.instructor.id ?? null)
  // Name + photo arrive with the navigation (see instructorLinkState), so the
  // hero paints -- and the avatar can morph into it -- before the fetch lands.
  const preview = (useLocation().state as { instructor?: InstructorPreview } | null)?.instructor
  const instructor = data?.instructor ?? preview
  const name = instructor ? splitName(instructor.name) : null

  return (
    <div className="overflow-x-clip">
      <PageHero
        back={{ to: '/', label: 'Back to overview' }}
        eyebrow="Instructor"
        title={name?.first ?? 'Loading'}
        accent={name?.last}
        description={data?.instructor.short_bio}
        media={
          <div
            style={id ? { viewTransitionName: `avatar-${id}` } : undefined}
            className="w-fit shrink-0 rounded-full bg-gradient-to-br from-primary via-[#e87ba4] to-[#eb6834] p-[3px]"
          >
            <Avatar className="size-28 border-4 border-background sm:size-40">
              <AvatarImage src={instructor?.image_url ?? undefined} alt="" />
              <AvatarFallback className="font-display text-3xl">
                {instructor ? initials(instructor.name) : ''}
              </AvatarFallback>
            </Avatar>
          </div>
        }
      >
        {data && data.top_artists.length > 0 && (
          <div className="pb-8">
            <NameMarquee compact duration={60} words={data.top_artists.map((a) => a.name)} />
          </div>
        )}
      </PageHero>

      {error && (
        <div className="mx-auto mt-8 max-w-7xl px-6">
          <p className="rounded-2xl border border-destructive/50 p-4 text-sm text-destructive">
            Couldn&apos;t load this instructor: {error}
          </p>
        </div>
      )}

      <section className={SECTION}>
        <SectionHeading index="01" title="The" accent="numbers." />
        {data ? (
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            <Tile icon={ActivitySquare} label={`${WORKOUTS_CAPITALIZED} catalogued`} value={data.totals.rides} size="lg" />
            <Tile delay={0.05} icon={Music2} label="Unique songs" value={data.totals.unique_songs} size="lg" />
            <Tile delay={0.1} icon={Mic2} label="Unique artists" value={data.totals.unique_artists} size="lg" />
            <Tile delay={0.15} icon={Gauge} label={`Avg. songs per ${WORKOUT}`} value={data.totals.avg_songs_per_ride} decimals={1} size="lg" />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-44 rounded-3xl" />
            ))}
          </div>
        )}
      </section>

      {data && data.themes.length > 0 && (
        <section className="mx-auto w-full max-w-7xl px-6 pb-16 sm:pb-24">
          <ThemeExplorer instructorId={data.instructor.id} themes={data.themes} options={options} />
        </section>
      )}

      <section className={SECTION}>
        <SectionHeading
          index="03"
          title="Redundancy"
          accent="score."
          blurb="How concentrated this instructor's song rotation is -- the higher these are, the more the same names keep coming back."
        />
        {data ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <ShareStat
              percent={data.concentration.top_artist_share_percent}
              label="of all song-plays are one single artist"
              delay={0}
            />
            <ShareStat
              percent={data.concentration.top_5_share_percent}
              label="of all song-plays come from their top 5 artists"
              delay={0.1}
            />
          </div>
        ) : (
          <Skeleton className="h-56 rounded-3xl" />
        )}
      </section>

      <section className={SECTION}>
        <SectionHeading index="04" title="On" accent="rotation." blurb="Their most-played artists, and the shape of the music and the classes." />
        <div className="grid grid-cols-1 gap-x-16 gap-y-12 lg:grid-cols-2">
          <div>
            <h3 className="mb-2 font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">Most-played artists</h3>
            {data ? (
              <RankedList
                unit={WORKOUTS}
                entries={data.top_artists.map((a) => ({ id: a.id, name: a.name, image_url: a.image_url, count: a.play_count }))}
              />
            ) : (
              <Skeleton className="h-96" />
            )}
          </div>
          <div className="flex flex-col gap-4">
            <Reveal>
              <SpotlightCard className="p-6 sm:p-8">
                <h3 className="mb-6 font-display text-2xl font-semibold tracking-tight">Genre mix</h3>
                {data ? <SoundMix data={data.genres} unitLabel="songs" /> : <Skeleton className="h-56" />}
              </SpotlightCard>
            </Reveal>
            <Reveal delay={0.1}>
              <SpotlightCard className="p-6 sm:p-8">
                <h3 className="mb-6 font-display text-2xl font-semibold tracking-tight">Popular {WORKOUT} formats</h3>
                {data ? <SoundMix data={data.ride_types} unitLabel={WORKOUTS} /> : <Skeleton className="h-56" />}
              </SpotlightCard>
            </Reveal>
          </div>
        </div>
      </section>

      {data && <RideTeaser instructorId={data.instructor.id} name={data.instructor.name} options={options} optionsFailed={optionsError !== null} />}
    </div>
  )
}
